import React, { Fragment } from 'react';
import ReactHtmlParser from 'react-html-parser';
import Modal from 'react-modal';

import classNames from 'classnames';
import { parseEntities } from 'parse-entities';
import { v4 as uuidv4 } from 'uuid';

import { HybridLink, Paragraph, TitleLink } from 'common/components';
import { IBlockItem, IFieldDescription } from 'common/interfaces/landing.interface';

import QuoteIcon from 'icons/table-quote.svg';

import './Table.scss';

interface ItemType extends Omit<
  IBlockItem,
  'id' | 'field_id' | 'field_description' | 'field_title' | 'field_image' | 'relationships'
> {
  field_title?: string;
  field_description?: IFieldDescription;
  customList?: React.ReactElement[];
  content?: React.ReactElement;
  topRowTitleClass?: string;
  customDescriptionUnderQuote?: string;
}

interface Props {
  items: ItemType[];
  tableClass?: string;
  isCounter?: boolean;
  isQuote?: boolean;
  firstCellFree?: boolean;
  lastCellFree?: boolean;
  hasRowTitle?: boolean;
  hasRowCounter?: boolean;
  titles?: string[];
  titleLinks?: (string | undefined)[];
  titleIcons?: React.ReactElement[];
  titleHover?: string[];
  titleText?: string[];
  titleClass?: string;
  listItemParagraphClass?: boolean;
  titleGridClass?: string;
  textGridClass?: string;
  tableTopBorderedClass?: string;
  titleColumnClass?: string;
  renderIcons?: (index: number) => React.ReactElement;
  summand?: number;
}

export const TableTopBorderedTitles = ({
  items,
  tableClass = '',
  isCounter = false,
  isQuote = false,
  firstCellFree = true,
  hasRowTitle = false,
  titleGridClass = '',
  textGridClass = '',
  tableTopBorderedClass = '',
  titleColumnClass = '',
  titles,
  titleLinks,
  titleIcons,
  titleHover,
  renderIcons,
  titleClass = 'fifth-level-title',
  listItemParagraphClass = false,
  summand = 1,
  hasRowCounter = false,
}: Props) => {
  const paragraphClassNames = classNames({
    paragraph: true,
    'paragraph-list-item-lh': listItemParagraphClass,
  });

  const [modalIsOpen, setIsOpen] = React.useState(false);
  const [currentHoverIndex, setCurrentIndex] = React.useState(0);
  const modalRef = React.useRef<HTMLDivElement>(null);

  function closeModal() {
    setIsOpen(false);
  }

  function openModal(currentIndex: number) {
    setCurrentIndex(currentIndex);
    setIsOpen(true);
  }

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (modalRef.current && !modalRef.current.contains(event.target as Node | null)) {
        closeModal();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [modalRef]);

  const modalSettings = {
    isOpen: modalIsOpen,
    onRequestClose: closeModal,
    overlayClassName: 'tooltip-modal-container',
    className: 'tooltip-modal-content',
    bodyOpenClassName: 'modal-opened',
    ariaHideApp: false,
    shouldCloseOnOverlayClick: true,
  };

  const basicHTMLRegex = /(<\/|\/>)/g;

  return (
    <div key={uuidv4()} className="table-overflow">
      <div
        className={classNames(
          'table-top-bordered-container',
          tableTopBorderedClass,
          { 'quote-table': isQuote && hasRowTitle },
          { 'has-titles': titles },
        )}
      >
        {titles && (
          <div
            key={uuidv4()}
            className={classNames('main-grid titles', {
              'has-double-title': !firstCellFree && titles.length >= 3 && !hasRowTitle,
            })}
          >
            {firstCellFree && <div key={uuidv4()} className="main-grid-item free" />}
            {titles.map((title, index) => (
              <div
                key={uuidv4()}
                className={classNames('main-grid-item table-title-columns', {
                  'table-title-columns-title-icon': titleIcons,
                })}
              >
                {!firstCellFree && titles.length >= 3 && !hasRowTitle && (
                  <h4 className="double-title">
                    {index === 0
                      ? `${parseEntities(title)}/${parseEntities(titles[1])}`
                      : titles[index + 1] && parseEntities(titles[index + 1])}
                  </h4>
                )}
                {titleLinks && typeof titleLinks[index] === 'string' ? (
                  <h4 className={titleColumnClass}>
                    <HybridLink to={titleLinks[index]!}>{parseEntities(title)}</HybridLink>
                  </h4>
                ) : (
                  <h4 className={`fourth-level-title ${titleColumnClass}`}>{parseEntities(title)}</h4>
                )}
                {titleIcons && (
                  <>
                    <div className="table-title-columns-icon desktop-only">
                      {titleIcons[index]}
                      <Paragraph
                        item={ReactHtmlParser(titleHover ? titleHover[index] : '')}
                        paragraphClass="table-title-columns-tooltip caption"
                        parseHtml
                      />
                    </div>
                    <div className="table-title-columns-icon device-only">
                      <button type="button" onClick={() => openModal(index)} aria-label="Open modal">
                        {titleIcons[index]}
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
        <div className={`table-top-bordered ${tableClass}`}>
          {items.map(
            (
              {
                field_title,
                field_description,
                field_list,
                field_link,
                customList,
                content,
                customDescriptionUnderQuote,
                topRowTitleClass = '',
              },
              index,
            ) => (
              <Fragment key={uuidv4()}>
                {hasRowTitle && (
                  <h4 key={uuidv4()} className={`table-top-row-title ${topRowTitleClass}`}>
                    {hasRowCounter && (
                      <span key={uuidv4()} className="title-number">
                        {index + summand}
                      </span>
                    )}
                    {field_title}
                  </h4>
                )}
                <div
                  className={classNames('main-grid-item small-device-only', titleGridClass, {
                    'hidden-title':
                      (!firstCellFree && titles && titles.length < 3) ||
                      (!isQuote &&
                        titles &&
                        ((field_list?.length && field_list?.length <= 1) ||
                          (customList?.length && customList?.length <= 1) ||
                          field_description)) ||
                      (isQuote && titles && titles?.length > 0),
                  })}
                >
                  {field_link ? (
                    <>
                      {isCounter && (
                        <span key={uuidv4()} className="title-number">
                          {index + summand}
                        </span>
                      )}
                      <TitleLink key={uuidv4()} title={field_title!} link={field_link} titleClass={titleClass} />
                    </>
                  ) : (
                    !hasRowTitle && (
                      <div
                        key={uuidv4()}
                        className={classNames(
                          { 'table-first-column-counter': isCounter },
                          { 'table-quote-title': isQuote },
                        )}
                      >
                        {isCounter && (
                          <span key={uuidv4()} className="title-number">
                            {index + summand}
                          </span>
                        )}
                        <h5 key={uuidv4()} className={titleClass}>
                          {field_title && parseEntities(field_title)}
                        </h5>
                      </div>
                    )
                  )}
                  {(customList !== undefined || field_list !== undefined) &&
                    (field_description && isQuote ? (
                      <div key={uuidv4()} className="table-quote-grid">
                        <div className="table-quote-wrapper">
                          <QuoteIcon />
                        </div>
                        <div className="table-quote-text">
                          {field_description &&
                            (field_description.value.match(basicHTMLRegex) ? (
                              ReactHtmlParser(field_description.value)
                            ) : (
                              <p>{parseEntities(field_description.value)}</p>
                            ))}
                        </div>
                      </div>
                    ) : (
                      field_description && (
                        <p className="paragraph table-description">{parseEntities(field_description.value)}</p>
                      )
                    ))}
                </div>
                <div
                  key={uuidv4()}
                  className={classNames(
                    'main-grid table-top-bordered-row',
                    { 'has-row-title': hasRowTitle },
                    { 'has-one-item': field_list?.length === 1 || customList?.length === 1 || field_description },
                  )}
                >
                  <div
                    className={classNames('main-grid-item', titleGridClass, {
                      'desktop-tablet-only': !isQuote || (isQuote && !titles),
                    })}
                  >
                    {field_link ? (
                      <>
                        {isCounter && (
                          <span key={uuidv4()} className="title-number">
                            {index + summand}
                          </span>
                        )}
                        <TitleLink key={uuidv4()} title={field_title!} link={field_link} titleClass={titleClass} />
                      </>
                    ) : (
                      !hasRowTitle &&
                      field_title && (
                        <div
                          key={uuidv4()}
                          className={classNames(
                            { 'table-first-column-counter': isCounter },
                            { 'table-quote-title': isQuote },
                          )}
                        >
                          {isCounter && (
                            <span key={uuidv4()} className="title-number">
                              {index + summand}
                            </span>
                          )}
                          <h5 key={uuidv4()} className={titleClass}>
                            {field_title && parseEntities(field_title)}
                          </h5>
                        </div>
                      )
                    )}
                    {(customList !== undefined || field_list !== undefined) &&
                      (field_description && isQuote ? (
                        <>
                          <div key={uuidv4()} className="table-quote-grid">
                            <div className="table-quote-wrapper">
                              <QuoteIcon />
                            </div>

                            <div className="table-quote-text">
                              {field_description &&
                                (field_description.value.match(basicHTMLRegex) ? (
                                  ReactHtmlParser(field_description.value)
                                ) : (
                                  <p>{parseEntities(field_description.value)}</p>
                                ))}
                            </div>
                          </div>
                          {customDescriptionUnderQuote && (
                            <p className="paragraph table-description description-after-quote">
                              {parseEntities(customDescriptionUnderQuote)}
                            </p>
                          )}
                        </>
                      ) : (
                        field_description && <p className="paragraph">{parseEntities(field_description.value)}</p>
                      ))}
                  </div>
                  {field_list &&
                    field_list.map((listItem) => (
                      <div key={uuidv4()} className="main-grid-item">
                        <p className={paragraphClassNames}>
                          {listItem.match(basicHTMLRegex) ? ReactHtmlParser(listItem) : parseEntities(listItem)}
                        </p>
                      </div>
                    ))}
                  {customList &&
                    customList.map((customListItem) => (
                      <div key={uuidv4()} className="main-grid-item">
                        {customListItem}
                      </div>
                    ))}
                  {renderIcons && renderIcons(index)}
                  {customList === undefined && field_list === undefined && (
                    <>
                      <div
                        key={uuidv4()}
                        className={classNames('main-grid-item', `${textGridClass}`, { hidden: !field_description })}
                      >
                        {field_description &&
                          (field_description.value.match(basicHTMLRegex) ? (
                            ReactHtmlParser(field_description.value)
                          ) : (
                            <p>{parseEntities(field_description.value)}</p>
                          ))}
                      </div>
                      {content}
                    </>
                  )}
                </div>
              </Fragment>
            ),
          )}
        </div>
        {titles && titles[currentHoverIndex] && titleHover && (
          <Modal {...modalSettings}>
            <div ref={modalRef} className="tooltip-modal-inside-text">
              <h6 className="h6-ol">{titles[currentHoverIndex]}</h6>
              <Paragraph item={ReactHtmlParser(titleHover[currentHoverIndex])} paragraphClass="" parseHtml />
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};
